/**
 * To use the Tree interface, you need to define two types:
 * - LeafType
 * - ParentType
 *
 * Doing so will give you access to all the functions in this module.
 */

import { type MaybePromise } from "@/types";

export interface LeafType {
  isLeaf: true;
}

export interface ParentType<L extends LeafType> {
  isLeaf: false;
  children: (L | this)[];
}

export type Tree<L extends LeafType, P extends ParentType<L>> = L | P;

export function isLeafNode<L extends LeafType, P extends ParentType<L>>(
  node: Tree<L, P>,
): node is L {
  return node.isLeaf;
}

export function treeMap<
  LIn extends LeafType,
  PIn extends ParentType<LIn>,
  LOut extends LeafType,
  POut extends ParentType<LOut>,
>(
  root: LIn | PIn,
  leafMapper: (leaf: LIn) => LOut,
  parentMapper: (parent: PIn, children: Tree<LOut, POut>[]) => POut,
): Tree<LOut, POut> {
  if (isLeafNode(root)) {
    return leafMapper(root);
  }

  // Hack to get around the type guard `isLeafNode` not 100% working properly
  const parent = root as PIn;

  const children = parent.children.map((node) =>
    treeMap(node, leafMapper, parentMapper),
  );

  return parentMapper(parent, children);
}

export async function treeMapPromise<
  LIn extends LeafType,
  PIn extends ParentType<LIn>,
  LOut extends LeafType,
  POut extends ParentType<LOut>,
>(
  root: Tree<LIn, PIn>,
  leafMapper: (leaf: LIn) => MaybePromise<LOut>,
  parentMapper: (
    parent: PIn,
    children: Tree<LOut, POut>[],
  ) => MaybePromise<POut>,
): Promise<Tree<LOut, POut>> {
  if (isLeafNode(root)) {
    return leafMapper(root);
  }

  // Hack to get around the type guard `isLeafNode` not 100% working properly
  const parent = root as PIn;

  const children = await Promise.all(
    parent.children.map((node) =>
      treeMapPromise(node, leafMapper, parentMapper),
    ),
  );

  return parentMapper(parent, children);
}

export function treeWalk<L extends LeafType, P extends ParentType<L>>(
  root: Tree<L, P>,
  fn: (node: Tree<L, P>) => void,
): void {
  fn(root);
  if (!isLeafNode(root)) {
    // Hack to get around the type guard `isLeafNode` not 100% working properly
    const parent = root as P;
    for (const child of parent.children) {
      treeWalk(child, fn);
    }
  }
}

export function treeFlatMap<L extends LeafType, P extends ParentType<L>, T>(
  root: Tree<L, P>,
  fn: (node: Tree<L, P>) => T,
): T[] {
  const rv: T[] = [];
  treeWalk(root, (node) => rv.push(fn(node)));
  return rv;
}
